import type { WorkflowDefinition } from "../types";

/**
 * W11 — Track a merger or acquisition in AI answers.
 *
 * `shared`: a deal happens once to a property, and everyone watching the two
 * names is watching the same transition.
 *
 * The caveat is the product gap, stated rather than hidden: there is no
 * brand-merge tooling, so the acquired brand is a competitor before close and
 * an alias after it, and the flip ends its separate trend line. Export the
 * competitor view before flipping if the two-line history matters.
 */
export const mergerWorkflow: WorkflowDefinition = {
	id: "merger",
	wNumber: 11,
	title: "Track a merger or acquisition in AI answers",
	outcome:
		"Watch share of voice move between the two names, on one continuous line through the change.",
	audiences: ["pr"],
	uses: [
		"Competitors",
		"Key Messages",
		"Annotations",
		"Competitors dashboard",
		"Settings",
		"Citation Intelligence",
	],
	scope: "property",
	concurrency: "shared",
	progress: "explicit",
	// A deal is tracked on the acquirer, which needs its own competitor set and
	// its own identity to migrate: BRAND or PRODUCT. A PERSON does not merge, a
	// LOCATION inherits, CATEGORY has no focal subject.
	isApplicable: (mode) => mode.isBrand || mode.isProduct,
	articleSlug: "how-to-track-ai-visibility-through-a-merger",
	articleStatus: "published",
	caveats: [
		"There is no brand-merge tool yet. Turning the acquired brand into an alias ends its separate trend line.",
	],
	status: "live",
	steps: [
		{
			id: "acquired-as-competitor",
			title: "Track the acquired brand as a competitor",
			page: "Competitors",
			route: "/competitors",
			criterion: "You've added a competitor since you started.",
			why: "Before close the two brands are still two brands. The useful measure is how much answer space each one holds.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Add the acquired brand with its other names",
				"Add the brands both companies compete with",
				"Export the two-name comparison before close",
			],
			// The acquired brand is added *for this deal*: a competitor set the
			// account already had says nothing about whether anyone has started
			// tracking the two names against each other.
			complete: { kind: "since", event: "competitorAdded" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Competitors tracked",
							value: state.property.competitorCount,
							target: 1,
						}
					: null,
		},
		{
			id: "deal-messages",
			title: "Write the deal story as key messages",
			page: "Key Messages",
			route: "/key-messages",
			criterion: "You've added a key message since you started.",
			why: "A deal announcement is a claim about why the two are better together. Written down, it becomes something you can measure.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Write the combined-company claims as positive messages",
				"Add the misreadings you expect as negative messages",
				"Keep each one short enough to match or not",
			],
			// Both halves of the deal narrative matter here — the combined-company
			// claims and the misreadings — so this stays sentiment-agnostic and
			// only asks that the writing happened after the run began.
			complete: { kind: "since", event: "keyMessageAdded" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.keyMessages.positive} deal claims tracked`,
									done:
										state.property.keyMessages.positive > 0,
								},
								{
									label: `${state.property.keyMessages.negative} misreadings tracked`,
									done:
										state.property.keyMessages.negative > 0,
								},
							],
						}
					: null,
		},
		{
			id: "annotate",
			title: "Mark the announcement and the close",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've noted a deal milestone since you started.",
			why: "Deal coverage arrives in bursts and moves both names at once. Dated marks tell the announcement apart from the close.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Mark the announcement, the clearance and the close",
				"Mark the day the acquired site starts redirecting",
				"Mark major coverage on the day it lands",
			],
			complete: { kind: "since", event: "annotationCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Milestones on the timeline",
							value: state.property.manualAnnotationCount,
						}
					: null,
		},
		{
			id: "watch-sov",
			title: "Watch share of voice move between the names",
			page: "Competitors",
			route: "/dashboard/competitors",
			criterion: "You've got daily data for this brand.",
			why: "Leadership will ask whether the combined company holds more answer space than the two halves did. This chart answers it.",
			docsHref: "/docs/dashboards/brand-dashboard",
			tasks: [
				"Chart both names against the rest of the set weekly",
				"Watch whether the acquired share transfers or disappears",
				"Export the view before you turn the name into an alias",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Daily data flowing",
									done: state.property.hasNightlyMetrics,
								},
								{
									label: `${state.property.competitorCount} names on the chart`,
									done: state.property.competitorCount > 0,
								},
							],
						}
					: null,
		},
		{
			id: "alias-post-close",
			title: "Add the acquired name as an alias after close",
			page: "Settings",
			route: "/settings?tab=general",
			criterion: "You've added at least one other name.",
			why: "Once the acquired brand retires, mentions of the old name are mentions of the brand you track. Only an alias makes them count.",
			docsHref: "/docs/dashboards/settings",
			tasks: [
				"Add the acquired name and its common variants",
				"Remove it from the competitor set the same day",
				"Keep the exported comparison somewhere findable",
			],
			// Count, not `since`: an alias carries no creation timestamp in the
			// observed state. Allowlisted in `catalog.test.ts`.
			complete: { kind: "count", key: "aliasCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Other names counted as yours",
							value: state.property.aliasCount,
							target: 1,
						}
					: null,
		},
		{
			id: "sources",
			title: "Find the sources carrying the deal story",
			page: "Citation Intelligence",
			route: "/citation-intelligence",
			criterion: "You've read the deal's source list.",
			why: "Deal coverage is concentrated, and a wrong figure in one story spreads for months. Ranking sources tells you which correction is worth chasing.",
			docsHref: "/docs/dashboards/citation-intelligence",
			tasks: [
				"Filter citations to the announcement window",
				"Check the outlets carrying a wrong price or buyer first",
				"Fix in order of placement value, not fame",
			],
			complete: { kind: "manual" },
		},
	],
};
